import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface StormReport {
  type: 'hail' | 'wind' | 'tornado';
  date: string;
  time: string;
  magnitude: number;
  location: string;
  county: string;
  state: string;
  lat: number;
  lng: number;
  comments: string;
  distance_miles?: number;
}

export interface StormStats {
  total: number;
  hail: number;
  wind: number;
  tornado: number;
  maxHail: number;
  maxWind: number;
}

export function useStormReports(options?: {
  days?: number;
  centerLat?: number;
  centerLng?: number;
  radiusMiles?: number;
  autoRefresh?: boolean;
}) {
  const [reports, setReports] = useState<StormReport[]>([]);
  const [stats, setStats] = useState<StormStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase.functions.invoke('noaa-storm-reports', {
        body: {
          days: options?.days ?? 7,
          centerLat: options?.centerLat,
          centerLng: options?.centerLng,
          radiusMiles: options?.radiusMiles,
        },
      });
      if (error) throw error;
      setReports(data.reports || []);
      setStats(data.stats || null);
    } catch (e: any) {
      setError(e.message || 'Failed to load storm reports');
    } finally {
      setLoading(false);
    }
  }, [options?.days, options?.centerLat, options?.centerLng, options?.radiusMiles]);

  useEffect(() => {
    fetchReports();
    if (options?.autoRefresh) {
      const id = setInterval(fetchReports, 5 * 60 * 1000); // 5min
      return () => clearInterval(id);
    }
  }, [fetchReports, options?.autoRefresh]);

  return { reports, stats, loading, error, refetch: fetchReports };
}
